import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import dayjs from 'dayjs'
import { CircularProgress } from '@material-ui/core'
import SydModal from '../../../../../../commonDesign/SydModal'
import SydModalActions from '../../../../../../commonDesign/SydModal/SydModalActions'
import SydButton from '../../../../../../commonDesign/Button'
import SydLabel, { hookFormErrorAdapter } from '../../../../../../commonDesign/SydLabel'
import SydDatePicker from '../../../../../../commonDesign/SydDatePicker'
import SydSelect from '../../../../../../commonDesign/SydSelect'
import SydInput from '../../../../../../commonDesign/SydInput'
import {
  useGetStatusReportSummary,
  useGetStatusReportSummaryConfigs,
  useListClientStatusReports
} from '../../../../../../../api/groups'
import { useCreateEntry, useWealthJourneyEntryTypes } from '../../../../../../../api/wealthJourney'
import WealthJourneyAttributeField from './WealthJourneyAttributeField'
import { mapAttributes } from './utils'

function CreateWealthJourneyEntryModal ({
  open,
  onClose,
  statusReportId
}) {
  const { data: statusReport, isLoading: isLoadingStatusReport } = useListClientStatusReports({
    filters: { statusReportId }
  }, {
    select: (data) => data?.statusReports?.[0]
  })

  const form = useForm({
    defaultValues: {
      summaryConfigId: null,
      entryText: '',
      entryDate: dayjs().format('YYYY-MM-DD'),
      entryTypeId: '',
      entryJson: {}
    }
  })
  const selectedEntryTypeId = form.watch('entryTypeId')

  const [mappedAttributeIds, setMappedAttributeIds] = useState(new Set())
  const scrollContainerRef = React.useRef(null)

  const { data: summaryConfigs = [], isLoading: configsLoading } = useGetStatusReportSummaryConfigs(
    { purposeKey: 'wealth-journey-entry' },
    {
      enabled: open,
      select: (data) => data?.configs ?? []
    }
  )
  const defaultWJConfig = useMemo(() => summaryConfigs?.length ? summaryConfigs.find(config => config.isDefault) || summaryConfigs[0] : null, [summaryConfigs])

  const { data: entryTypes = [], isLoading: entryTypesLoading } = useWealthJourneyEntryTypes()
  const selectedEntryType = useMemo(() => {
    if (!selectedEntryTypeId || !entryTypes.length) return null
    return entryTypes.find(et => et.entryTypeId === selectedEntryTypeId)
  }, [selectedEntryTypeId, entryTypes])

  const enabledAttributes = useMemo(() => {
    if (!selectedEntryType?.attributes) return []
    return selectedEntryType.attributes
      .filter(attr => attr.enabled)
      .sort((a, b) => a.ordinal - b.ordinal)
  }, [selectedEntryType])

  useEffect(() => {
    if (open && defaultWJConfig) {
      form.setValue('summaryConfigId', defaultWJConfig.summaryConfigId)
    }
  }, [open, defaultWJConfig, form])

  useEffect(() => {
    if (entryTypes?.length && !selectedEntryTypeId) {
      form.setValue('entryTypeId', entryTypes[0].entryTypeId)
    }
  }, [entryTypes, form, selectedEntryTypeId])

  // Scroll to top when entry type changes
  useLayoutEffect(() => {
    if (scrollContainerRef.current) {
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = 0
        }
      })
    }
  }, [selectedEntryTypeId])

  const { data: summaryData, isLoading: summaryLoading } = useGetStatusReportSummary(
    { statusReportId, summaryConfigId: defaultWJConfig?.summaryConfigId },
    {
      enabled: open && !!defaultWJConfig?.summaryConfigId && !!statusReportId
    }
  )

  useEffect(() => {
    if (!open) return

    const mappedValues = mapAttributes({
      summaryData,
      attributes: selectedEntryType?.attributes
    })
    if (!mappedValues) return

    const { attributeValues, usedAttributeIds, titleAttr } = mappedValues
    if (titleAttr && form.getValues(titleAttr.accessor)) {
      form.setValue('entryText', form.getValues(titleAttr.accessor))
    } else if (summaryData.markdown) {
      const firstLine = summaryData.markdown.split('\n')[0].replace(/^#+ /, '').trim()
      form.setValue('entryText', firstLine)
    }
    Object.keys(attributeValues).forEach(key => {
      form.setValue(key, attributeValues[key])
    })
    setMappedAttributeIds(usedAttributeIds)
  }, [summaryData, selectedEntryType, open, form])

  const { mutateAsync: createWJEntry, isLoading: isCreating } = useCreateEntry()

  const onSubmit = async (formData) => {
    await createWJEntry({
      clientId: statusReport?.levelId,
      entryTypeId: Number(formData.entryTypeId),
      entryDate: formData.entryDate,
      entryText: formData.entryText,
      entryJson: {
        ...(formData.entryJson || {}),
        bodyType: 'markdown',
        statusReportId,
        summaryConfigId: formData.summaryConfigId,
        source: 'status-report-finalization'
      }
    })

    onClose()
  }

  // Show loading while waiting for data
  if (configsLoading || entryTypesLoading || isLoadingStatusReport || summaryLoading) {
    return (
      <SydModal
        open={open}
        onClose={onClose}
        title='Create Wealth Journey Entry'
        size='large'
      >
        <div
          className='flex flex-col items-center justify-center py-15'
          style={{ height: 'calc(100vh - 250px)' }}
        >
          <CircularProgress size={24} />
          <span className='mt-3 text-neutral-600'>Loading...</span>
        </div>
      </SydModal>
    )
  }

  return (
    <SydModal
      open={open}
      onClose={onClose}
      title='Create Wealth Journey Entry'
      subtitle="Create a wealth journey entry from this status report's summary."
      size='large'
    >
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className='flex flex-col gap-5' style={{ height: 'calc(100vh - 250px)' }}>
          <div ref={scrollContainerRef} className='flex flex-col gap-5 flex-1' style={{ overflowY: 'scroll' }}>
            <div className='flex gap-5 flex-wrap'>
              <div>
                <Controller
                  control={form.control}
                  name='entryTypeId'
                  rules={{ required: 'Entry type is required' }}
                  render={({ field, fieldState }) => (
                    <SydLabel
                      label='Entry Type'
                      description='Type of wealth journey entry to create'
                      error={hookFormErrorAdapter(form, fieldState)}
                    >
                      <SydSelect
                        {...field}
                        size='sm'
                        options={entryTypes?.map(et => ({
                          label: et.entryTypeName,
                          value: et.entryTypeId
                        })) ?? []}
                        placeholder='Select entry type...'
                      />
                    </SydLabel>
                  )}
                />
              </div>

              <div>
                <Controller
                  control={form.control}
                  name='entryDate'
                  rules={{ required: 'Entry date is required' }}
                  render={({ field, fieldState }) => (
                    <SydLabel
                      label='Entry Date'
                      description='Date for this wealth journey entry'
                      error={hookFormErrorAdapter(form, fieldState)}
                    >
                      <SydDatePicker {...field} size='sm' />
                    </SydLabel>
                  )}
                />
              </div>

              <div className='flex-1'>
                <Controller
                  control={form.control}
                  name='entryText'
                  rules={{ required: 'Entry title is required' }}
                  render={({ field, fieldState }) => (
                    <SydLabel
                      label='Entry Title'
                      description='Brief title or description for this entry'
                      error={hookFormErrorAdapter(form, fieldState)}
                    >
                      <SydInput {...field} size='sm' placeholder='Enter a title...' />
                    </SydLabel>
                  )}
                />
              </div>
            </div>

            {enabledAttributes.length > 0 && (
              <div className='flex flex-col gap-5'>
                {enabledAttributes.map(attr => (
                  <WealthJourneyAttributeField
                    key={attr.accessor}
                    mappedAttributeIds={mappedAttributeIds}
                    attribute={attr}
                  />
                ))}
              </div>
            )}
          </div>

          <SydModalActions>
            <SydButton
              onClick={onClose}
              variant='secondary'
              disabled={isCreating}
            >
              Cancel
            </SydButton>
            <SydButton
              type='submit'
              variant='primary'
              processing={isCreating}
              disabled={isCreating}
            >
              Create Entry
            </SydButton>
          </SydModalActions>
        </form>
      </FormProvider>
    </SydModal>
  )
}

CreateWealthJourneyEntryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  statusReportId: PropTypes.number.isRequired
}

export default CreateWealthJourneyEntryModal
